import {intlShape, utils} from '@wallaby-ui/core';
import classNames from 'classnames';
import {format, parse} from 'date-fns';
import {isEqual, isNil} from 'lodash';
import PropTypes from 'prop-types';
import * as React from 'react';
import DatePicker from 'react-datepicker';
import {injectIntl} from 'react-intl';

import styles from './FilterNodeTypeDateMonth.less';
import {FilterNodeTypeWrapper} from './FilterNodeTypeWrapper';
import {FilterSubmitButton} from './FilterSubmitButton';
import {DynamicFilter, Filter, FILTER_TYPE_SELECT} from 'src/js/lib/filtering';
import {FilterNode as FilterNodeModel} from 'src/js/model/filters_config';

/**
 * Use "type": "date_month" in config
 */
class FilterNodeTypeDateMonth extends React.PureComponent {
  constructor(props) {
    super(props);
    this.handleChange = this.handleChange.bind(this);
    this.handleButtonSubmit = this.handleButtonSubmit.bind(this);

    this.state = {
      minDate: null,
      maxDate: null,
      suppressFocus: false,
      value: null,
      ...this.getBounds(),
    };
  }

  static getDerivedStateFromProps(props, state) {
    let newState = null;
    if (props.selectedFilter) {
      const newDate = props.selectedFilter.getValue()
        ? parse(
            props.selectedFilter.getValue(),
            utils.DateUtils.MONTH_FORMAT,
            new Date()
          )
        : null;
      if (!isEqual(newDate, state.prevValue)) {
        newState = {
          ...newState,
          value: newDate,
          prevValue: newDate,
        };
      }
    }
    if (!isEqual(props.isOpened, state.prevIsOpened)) {
      newState = {
        ...newState,
        suppressFocus: !props.isOpened || state.prevIsOpened,
        prevIsOpened: props.isOpened,
      };
    }

    return newState;
  }

  handleChange(value) {
    this.setState({value, suppressFocus: true});
  }

  handleButtonSubmit() {
    this.changeFilter(this.getValue());
  }

  getBounds() {
    const {dataSets, node} = this.props;
    const dataSetId = node.getChildrenDbKey();
    let result;

    if (dataSets) {
      // return promise
      const values = dataSets[dataSetId];
      let minDate = null;
      let maxDate = null;
      if (values.length > 0) {
        // dates in dataSet are sorted in descending order
        maxDate = parse(
          values[0].name,
          utils.DateUtils.MONTH_FORMAT,
          new Date()
        );
        minDate = parse(
          values[values.length - 1].name,
          utils.DateUtils.MONTH_FORMAT,
          new Date()
        );
      }
      result = {minDate, maxDate};
    }
    return result;
  }

  getFilter(value) {
    const {node} = this.props;
    const strValue = value
      ? format(new Date(value), utils.DateUtils.MONTH_FORMAT)
      : null;
    const filterType = FILTER_TYPE_SELECT;
    const SelectFilter = DynamicFilter.getFilterClass(filterType);
    return new SelectFilter({
      node,
      type: filterType,
      property: node.getName(),
      value: strValue,
    });
  }

  getValue() {
    const {value} = this.state;
    return value;
  }

  isDisabled() {
    const {value} = this.state;
    return value == null;
  }

  changeFilter(value) {
    const {changeFilter} = this.props;
    const filter = this.getFilter(value);
    changeFilter(filter);
  }

  render() {
    const {
      inputRef,
      intl,
      isOpened,
      node,
      removeFilter,
      selectedFilter,
      setFocus,
      toggleOpened,
    } = this.props;
    const {minDate, maxDate, suppressFocus} = this.state;
    const value =
      this.getValue() ||
      (node.getDefaultValue() ? new Date(node.getDefaultValue().value) : null);
    const filter = this.getFilter(value);
    const locale = utils.DateUtils.translateLocaleToDateFnsLocale(intl.locale);
    const additionalProperties = {
      ...(isNil(minDate) ? {} : {minDate}),
      ...(isNil(maxDate) ? {} : {maxDate}),
    };

    return (
      <FilterNodeTypeWrapper
        filter={filter}
        isOpened={isOpened}
        node={node}
        removeFilter={removeFilter}
        setFocus={setFocus}
        selectedFilter={selectedFilter}
        suppressFocus={suppressFocus}
        toggleOpened={toggleOpened}
      >
        <div
          className={classNames(
            'dlFilterNodeContentInput',
            styles.dlFilterNodeTypeDateRangeInputWrapper
          )}
        >
          <div className={styles.dlInnerDataPickerWrapper}>
            <DatePicker
              className={styles.dlFilterNodeTypeDateRangeInput}
              onChange={this.handleChange}
              disabledKeyboardNavigation
              dateFormat={utils.DateUtils.MONTH_FORMAT}
              locale={locale}
              ref={(input) => inputRef(input ? input.input : null)}
              selected={value}
              tabIndex={isOpened ? 0 : -1}
              showMonthYearPicker
              utc
              {...additionalProperties}
            />
          </div>
          <i
            className={classNames(
              'far fa-calendar-alt',
              styles.dlFilterNodeTypeDateRangeIcon
            )}
          />

          <FilterSubmitButton
            onClick={this.handleButtonSubmit}
            isDisabled={this.isDisabled()}
            tabIndex={isOpened ? 0 : -1}
          />
        </div>
      </FilterNodeTypeWrapper>
    );
  }
}

FilterNodeTypeDateMonth.displayName = 'FilterNodeTypeDateMonth';

FilterNodeTypeDateMonth.propTypes = {
  dataSets: PropTypes.objectOf(PropTypes.any),
  node: PropTypes.instanceOf(FilterNodeModel).isRequired,
  changeFilter: PropTypes.func.isRequired,
  inputRef: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
  isOpened: PropTypes.bool,
  removeFilter: PropTypes.func.isRequired,
  selectedFilter: PropTypes.instanceOf(Filter),
  setFocus: PropTypes.func.isRequired,
  toggleOpened: PropTypes.func.isRequired,
};

FilterNodeTypeDateMonth.defaultProps = {
  dataSets: undefined,
  isOpened: false,
  selectedFilter: undefined,
};

export default injectIntl(FilterNodeTypeDateMonth);
